<template>
  <div class="orders-tab">
    <div class="orders-tab__bordered-container">
    <!-- Sub-tabs + Archived -->
    <Tab
      class="orders-tab__header"
      type="outlined"
      :modelValue="activeTabValue"
      :tabs="tabConfig"
      :indie_tab="archivedTabConfig"
      :group_padding="0"
      @change="onTabChange"
    />

    <!-- Filters -->
    <div class="orders-tab__filters">
      <div class="orders-tab__filters-left">
        <div class="orders-tab__filter-group">
          <label class="orders-tab__filter-label">Filter By Provider Name</label>
          <SingleSelector
            class="orders-tab__filter-select"
            :modelValue="provider"
            :options="providerOptions"
            :placeholder="providerPlaceholder"
            size="large"
            @update:modelValue="changeProvider"
          />
        </div>
        <div class="orders-tab__filter-group">
          <label class="orders-tab__filter-label">Filter By Type</label>
          <SingleSelector
            class="orders-tab__filter-select"
            :modelValue="typeValue"
            :options="typeOptions"
            :placeholder="typeOptions.find(o => o.value === typeValue)?.label || 'All'"
            size="large"
            @update:modelValue="changeType"
          />
        </div>
      </div>
      <div v-if="selectedIds.length > 0" class="orders-tab__filters-action">
        <ResponsiveButton
          button_type="outlined"
          built_in_theme="dark"
          size="medium"
          :disabled="loading"
          :display_name="isArchived ? 'Restore by Batch' : 'Archive by Batch'"
          @click-button="batchAction"
        >
          <template #prefix>
            <svg
              v-if="!isArchived"
              width="16"
              height="16"
              viewBox="0 0 18 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M15.75 5.25H2.25V3H15.75V5.25ZM14.25 6V15C14.25 15.825 13.575 16.5 12.75 16.5H5.25C4.425 16.5 3.75 15.825 3.75 15V6H14.25ZM7.5 8.25H6V13.5H7.5V8.25ZM12 8.25H10.5V13.5H12V8.25Z" fill="currentColor"/>
            </svg>
            <svg
              v-else
              width="16"
              height="16"
              viewBox="0 0 19 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M12.22 7.85571C12.22 6.89557 11.4344 6.11 10.4743 6.11C9.51414 6.11 8.72857 6.89557 8.72857 7.85571C8.72857 8.81586 9.51414 9.60143 10.4743 9.60143C11.4344 9.60143 12.22 8.81586 12.22 7.85571ZM10.4743 0C6.13619 0 2.61857 3.51761 2.61857 7.85571H0L3.49143 11.3471L6.98286 7.85571H4.36429C4.36429 4.47776 7.09633 1.74571 10.4743 1.74571C13.8522 1.74571 16.5843 4.47776 16.5843 7.85571C16.5843 11.2337 13.8522 13.9657 10.4743 13.9657C9.15627 13.9657 7.93427 13.538 6.93049 12.831L5.69103 14.0879C7.01777 15.1004 8.6762 15.7114 10.4743 15.7114C14.8124 15.7114 18.33 12.1938 18.33 7.85571C18.33 3.51761 14.8124 0 10.4743 0Z" fill="currentColor"/>
            </svg>
          </template>
        </ResponsiveButton>
      </div>
    </div>

    <!-- Select All + Notification bar -->
    <div class="orders-tab__toolbar">
      <div v-if="!loading && items.length > 0" class="orders-tab__select-all">
        <Checkbox
          :modelValue="isSelectAllChecked"
          built_in_theme="primary-blue"
          label="Select All"
          @change="onSelectAllChange"
        />
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="orders-tab__loading">
      <Loading :loading="true" loadingText="Loading orders..." />
    </div>

    <!-- Error -->
    <div v-else-if="error" class="orders-tab__error">
      <div class="orders-tab__error-box">
        <span>{{ error }}</span>
        <button class="orders-tab__retry" @click="retry">Retry</button>
      </div>
    </div>

    <!-- Empty -->
    <div v-else-if="items.length === 0" class="orders-tab__empty">
      <p>No orders yet</p>
    </div>

    <!-- Table -->
    <div v-else class="orders-tab__table-wrapper">
      <table class="orders-tab__table">
        <thead>
          <tr>
            <th class="orders-tab__th orders-tab__th--checkbox"></th>
            <th class="orders-tab__th">Time</th>
            <th class="orders-tab__th">Status</th>
            <th class="orders-tab__th">Provider Name</th>
            <th class="orders-tab__th">Patient's Name</th>
            <th class="orders-tab__th">Accession ID</th>
            <th class="orders-tab__th">Service Date</th>
            <th class="orders-tab__th">Actions</th>
          </tr>
        </thead>
        <tbody>
          <!-- Notification bar inside table -->
          <tr v-if="showNotificationBar" class="orders-tab__notification-row">
            <td colspan="8">
              <div class="orders-tab__notification-bar">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <g clip-path="url(#clip0_notif)">
                    <path d="M2.73804 17.9979C1.96611 19.3313 2.92823 21 4.46889 21H19.5311C21.0718 21 22.0339 19.3313 21.262 17.9979L13.7309 4.98965C12.9605 3.65908 11.0395 3.65908 10.2691 4.98965L2.73804 17.9979ZM13 17C13 17.5523 12.5523 18 12 18C11.4477 18 11 17.5523 11 17C11 16.4477 11.4477 16 12 16C12.5523 16 13 16.4477 13 17ZM13 13C13 13.5523 12.5523 14 12 14C11.4477 14 11 13.5523 11 13V11C11 10.4477 11.4477 10 12 10C12.5523 10 13 10.4477 13 11V13Z" fill="#E0961E"/>
                  </g>
                  <defs>
                    <clipPath id="clip0_notif">
                      <rect width="24" height="24" fill="white"/>
                    </clipPath>
                  </defs>
                </svg>
                <span>You have {{ currentSubTabUnread }} new notifications.</span>
              </div>
            </td>
          </tr>
          <tr
            v-for="order in items"
            :key="order.id"
            :class="['orders-tab__row', { 'orders-tab__row--new': !order.read }]"
            @click="handleRowClick(order)"
          >
            <td class="orders-tab__td orders-tab__td--checkbox" @click.stop>
              <Checkbox
                :modelValue="selectedIds.includes(order.id)"
                :isLabelClickable="false"
                built_in_theme="primary-blue"
                @change="(checked) => onRowSelectChange(order.id, checked)"
              />
            </td>
            <td class="orders-tab__td">{{ order.time }}</td>
            <td class="orders-tab__td">
              <span :class="['orders-tab__status-badge', `orders-tab__status-badge--${order.statusType}`]">
                {{ order.status }}
              </span>
            </td>
            <td class="orders-tab__td">{{ order.providerName }}</td>
            <td class="orders-tab__td">{{ order.patientName }}</td>
            <td class="orders-tab__td">{{ order.accessionId }}</td>
            <td class="orders-tab__td">{{ order.serviceDate }}</td>
            <td class="orders-tab__td orders-tab__td--actions" @click.stop>
              <ResponsiveButton
                v-if="!isArchived"
                button_type="text"
                built_in_theme="dark"
                size="medium"
                display_name="Archive"
                @click-button="archiveOrder(order.id)"
              />
              <ResponsiveButton
                v-else
                button_type="text"
                built_in_theme="dark"
                size="medium"
                display_name="Restore"
                @click-button="restoreOrder(order.id)"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div v-if="total > 0" class="orders-tab__pagination">
      <Pagination
        :total_items="total"
        :current_page="currentPage"
        :per_page="limit"
        :per_page_options="[10, 20, 50]"
        item_label="notifications"
        @update:current_page="changePage"
        @update:per_page="changeLimit"
      />
    </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import Loading from '@/NewLibrary/Loading/Loading.vue';

const TYPE_OPTIONS_MAPPING = {
  reports: [
    { value: 'all', label: 'All' },
    { value: 'report_available', label: 'Report Available' },
    { value: 'report_updated', label: 'Report Updated' },
  ],
  operations: [
    { value: 'all', label: 'All' },
    { value: 'orders', label: 'Orders' },
    { value: 'shipping', label: 'Shipping' },
  ],
  archived: [
    { value: 'all', label: 'All' },
    { value: 'issues', label: 'Issues' },
    { value: 'reports', label: 'Reports' },
    { value: 'orders', label: 'Orders' },
    { value: 'shipping', label: 'Shipping' },
  ],
};

export default {
  name: 'OrdersTab',
  components: { Loading },
  data() {
    return {
      subTabs: [
        { value: 'issues', label: 'Issues' },
        { value: 'reports', label: 'Reports' },
        { value: 'operations', label: 'Operation' },
      ],
      selectedIds: [],
      notificationBarDismissed: false,
      notificationBarTimer: null,
    };
  },
  computed: {
    ...mapState('notifications', {
      items: state => state.orders.items,
      loading: state => state.orders.loading,
      error: state => state.orders.error,
      subTab: state => state.orders.subTab,
      isArchived: state => state.orders.isArchived,
      storeType: state => state.orders.type,
      provider: state => state.orders.provider,
      providerOptions: state => state.orders.providerOptions,
      limit: state => state.orders.limit,
      total: state => state.orders.total,
      tabsBadge: state => state.orders.tabsBadge,
      hasMore: state => state.orders.hasMore,
      cursorHistory: state => state.orders.cursorHistory,
      cursorIndex: state => state.orders.cursorIndex,
      issueTypeOptions: state => state.orders.issueTypeOptions,
      pageUnread: state => state.orders.pageUnread,
    }),
    typeValue() {
      return this.storeType || 'all';
    },
    activeTabValue() {
      return this.isArchived ? 'archived' : this.subTab;
    },
    tabConfig() {
      return this.subTabs.map(tab => {
        const unread = this.getSubTabUnread(tab.value);
        return {
          value: tab.value,
          label: tab.label,
          badge: unread > 0 ? unread : false,
        };
      });
    },
    archivedTabConfig() {
      return { value: 'archived', label: 'Archived' };
    },
    typeOptions() {
      if (this.isArchived) return TYPE_OPTIONS_MAPPING.archived;
      if (this.subTab === 'issues') return this.issueTypeOptions;
      return TYPE_OPTIONS_MAPPING[this.subTab] || [];
    },
    providerPlaceholder() {
      const opt = this.providerOptions.find(o => o.value === this.provider);
      return opt ? opt.label : 'All';
    },
    currentSubTabUnread() {
      return this.pageUnread || 0;
    },
    showNotificationBar() {
      return this.currentSubTabUnread > 0 && !this.notificationBarDismissed;
    },
    currentPage() {
      return this.cursorIndex + 1;
    },
    totalPages() {
      return Math.ceil(this.total / this.limit) || 1;
    },
    canNextPage() {
      return this.cursorIndex < this.cursorHistory.length - 1 || this.hasMore;
    },
    isSelectAllChecked() {
      return this.items.length > 0 && this.selectedIds.length === this.items.length;
    },
  },
  watch: {
    items(newItems) {
      const nextIds = newItems.map(item => item.id);
      this.selectedIds = this.selectedIds.filter(id => nextIds.includes(id));
    },
    showNotificationBar(val) {
      clearTimeout(this.notificationBarTimer);
      if (val) {
        this.notificationBarTimer = setTimeout(() => {
          this.notificationBarDismissed = true;
        }, 4000);
      }
    },
    subTab() {
      // notificationBarDismissed is reset in resetCursorAndFetch
    },
  },
  beforeUnmount() {
    clearTimeout(this.notificationBarTimer);
  },
  methods: {
    dismissNotificationBar() {
      this.notificationBarDismissed = true;
      clearTimeout(this.notificationBarTimer);
    },
    onSelectAllChange(checked) {
      this.selectedIds = checked ? this.items.map(item => item.id) : [];
    },
    getSubTabUnread(tabValue) {
      return this.tabsBadge?.[tabValue]?.unread || 0;
    },
    resetCursorAndFetch() {
      this.$store.commit('notifications/RESET_ORDERS_CURSOR');
      this.$store.commit('notifications/SET_ORDERS_PAGE_UNREAD', 0);
      this.notificationBarDismissed = false;
      this.$store.dispatch('notifications/fetchOrders');
    },
    changeSubTab(tab) {
      if (this.isArchived) {
        this.$store.commit('notifications/SET_ORDERS_IS_ARCHIVED', false);
      }
      this.$store.commit('notifications/SET_ORDERS_SUB_TAB', tab);
      this.$store.commit('notifications/SET_ORDERS_TYPE', 'all');
      this.resetCursorAndFetch();
    },
    toggleArchived() {
      const wasArchived = this.isArchived;
      this.$store.commit('notifications/SET_ORDERS_IS_ARCHIVED', !wasArchived);
      this.$store.commit('notifications/SET_ORDERS_TYPE', 'all');
      this.resetCursorAndFetch();
    },
    onTabChange(value) {
      if (value === 'archived') {
        if (!this.isArchived) this.toggleArchived();
      } else {
        this.changeSubTab(value);
      }
    },
    changeProvider(val) {
      this.$store.commit('notifications/SET_ORDERS_PROVIDER', val);
      this.resetCursorAndFetch();
    },
    changeType(val) {
      this.$store.commit('notifications/SET_ORDERS_TYPE', val);
      this.resetCursorAndFetch();
    },
    async changePage(page) {
      if (page < 1 || page > this.totalPages || page === this.currentPage) return;
      this.notificationBarDismissed = false;

      if (page < this.currentPage) {
        this.$store.commit('notifications/SET_ORDERS_CURSOR_INDEX', page - 1);
        await this.$store.dispatch('notifications/fetchOrders', { withBadge: false });
        return;
      }

      while (this.currentPage < page && this.canNextPage) {
        this.$store.commit('notifications/SET_ORDERS_CURSOR_INDEX', this.cursorIndex + 1);
        await this.$store.dispatch('notifications/fetchOrders', { withBadge: false });
      }
    },
    changeLimit(val) {
      this.$store.commit('notifications/SET_ORDERS_LIMIT', val);
      this.resetCursorAndFetch();
    },
    onRowSelectChange(id, checked) {
      const idx = this.selectedIds.indexOf(id);
      if (checked && idx < 0) this.selectedIds.push(id);
      else if (!checked && idx >= 0) this.selectedIds.splice(idx, 1);
    },
    handleRowClick(order) {
      if (!order?.id || order.read) return;
      this.$store.dispatch('notifications/markOrderAsRead', order.id);
    },
    archiveOrder(id) {
      this.$store.dispatch('notifications/archiveOrderItem', id);
    },
    restoreOrder(id) {
      this.$store.dispatch('notifications/batchUnarchiveOrders', [id]);
    },
    async batchAction() {
      if (this.selectedIds.length === 0) return;
      const ids = [...this.selectedIds];
      if (this.isArchived) {
        await this.$store.dispatch('notifications/batchUnarchiveOrders', ids);
      } else {
        await this.$store.dispatch('notifications/batchArchiveOrders', ids);
      }
      this.selectedIds = [];
    },
    retry() {
      this.$store.dispatch('notifications/fetchOrders');
    },
  },
};
</script>

<style scoped>
.orders-tab {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.orders-tab__bordered-container {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  border: 1px dashed #C4C4C4;
  border-radius: 8px;
  background: #FFFFFF;
  padding: 12px;
  gap: 12px;
}

/* === Section 2-3: Filters === */
.orders-tab__filters {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 56px;
  padding: 0 16px;
}

.orders-tab__filters-left {
  display: flex;
  gap: 12px;
  align-items: flex-end;
}

.orders-tab__filters-action {
  display: flex;
  align-items: center;
  padding-bottom: 2px;
}

.orders-tab__filter-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.orders-tab__filter-label {
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #4A4A4A;
  white-space: nowrap;
  letter-spacing: 0.1px;
}

.orders-tab__filter-select {
  flex: 0 0 260px;
}

.orders-tab__filter-select :deep(.single-selector-unit-wrapper) {
  width: 100%;
}

/* === Section 4: Toolbar + Select All === */
.orders-tab__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 2px 0;
}

.orders-tab__select-all {
  display: flex;
  align-items: center;
  padding-left: 12px;
  font-family: Inter, sans-serif;
  font-size: 14px;
  color: #333;
  letter-spacing: 0.25px;
}

/* === Loading / Empty / Error === */
.orders-tab__loading,
.orders-tab__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 40px 0;
  color: #999;
  font-family: Inter, sans-serif;
  font-size: 14px;
}

.orders-tab__error-box {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #FAEAEA;
  border: 1px solid #f5c6c6;
  border-radius: 8px;
  padding: 12px 16px;
  color: #D10000;
  font-family: Inter, sans-serif;
  font-size: 14px;
}

.orders-tab__retry {
  margin-left: auto;
  background: none;
  border: 1px solid #D10000;
  border-radius: 4px;
  color: #D10000;
  padding: 4px 12px;
  font-size: 12px;
  cursor: pointer;
}

/* === Table Wrapper + Scrollbar === */
.orders-tab__table-wrapper {
  flex: 1;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #c1c1c1 transparent;
}

.orders-tab__table-wrapper::-webkit-scrollbar {
  width: 4px;
}

.orders-tab__table-wrapper::-webkit-scrollbar-track {
  background: transparent;
}

.orders-tab__table-wrapper::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 2px;
}

.orders-tab__table-wrapper::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* === Section 10: Notification Bar === */
.orders-tab__notification-row td {
  padding: 0;
  border: none;
}

.orders-tab__notification-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  background: #FEF9E9;
  border-bottom: 1px solid #F7EECE;
  border-radius: 0 0 8px 8px;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 300;
  color: #333;
  letter-spacing: 0.25px;
}

.orders-tab__notification-bar svg {
  flex-shrink: 0;
}

/* === Section 5: Table + Header === */
.orders-tab__table {
  width: 100%;
  border-collapse: collapse;
  font-family: Inter, sans-serif;
}

.orders-tab__th {
  text-align: center;
  padding: 12px 8px;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.25px;
  color: #333;
  background: #E4F3F5;
  white-space: nowrap;
  border-right: 1px dashed #C4C4C4;
  height: 42px;
  box-sizing: border-box;
}

.orders-tab__th:last-child {
  border-right: none;
}

.orders-tab__th--checkbox {
  width: 44px;
  padding: 12px 8px;
}

/* === Section 6: Table Rows + Borders === */
.orders-tab__row {
  border-bottom: 1px dashed #C4C4C4;
  background: #fff;
  height: 52px;
}

.orders-tab__row:nth-child(even) {
  background: #F9FBFD;
}

.orders-tab__row:hover {
  background: #F0F4F8;
}

/* === Section 12: Table Cells === */
.orders-tab__td {
  padding: 12px 8px;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.25px;
  color: #333;
  vertical-align: middle;
  text-align: center;
  border-right: 1px dashed #C4C4C4;
}

.orders-tab__td:last-child {
  border-right: none;
}

.orders-tab__td--checkbox {
  width: 44px;
  padding: 12px 8px;
}

.orders-tab__td--checkbox :deep(.checkbox-container),
.orders-tab__td--actions :deep(.responsive-button) {
  margin: 0 auto;
}

/* === Section 7: Status Chips === */
.orders-tab__status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 400;
  white-space: nowrap;
  height: 28px;
  box-sizing: border-box;
  letter-spacing: 0.25px;
}

.orders-tab__status-badge--available {
  background: #E5F9EF;
  color: #1C96AD;
}

.orders-tab__status-badge--amended {
  background: #FAEAEA;
  color: #D10000;
}

/* === Section 8: New/Amended Row — no visual difference, all text #333 === */

/* === Section 11: Pagination === */
.orders-tab__pagination {
  flex-shrink: 0;
}
</style>
